<div class="cyber-scan-header-spectacular">
  <div class="cyber-header-icon">
    <i class="bi bi-check-circle-fill text-success"></i>
  </div>
  <div>
    <h1 class="h2 mb-1">Registration Complete!</h1>
    <p class="text-muted mb-0">Your Scan Ninja account is ready to use</p>
  </div>
</div>

<!-- Loading State -->
@if (loading) {
  <div class="text-center p-5">
    <div class="spinner-border text-cyber-accent" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-3 text-muted">Finalizing your account setup...</p>
  </div>
}

<!-- Error State -->
@if (error) {
  <div class="cyber-alert cyber-alert-danger">
    <div class="d-flex">
      <i class="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"></i>
      <div>
        <h6 class="cyber-alert-heading">Setup Error</h6>
        <p class="mb-0">
          There was an issue accessing your account information. Please contact
          support.
        </p>
      </div>
    </div>
  </div>
}

<!-- Success Content -->
@if (!loading && !error) {
  <div class="cyber-filters-spectacular">
    <div class="cyber-card">
      <!-- Account Summary -->
      <div class="account-summary-section mb-5">
        <div class="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h3 class="cyber-section-title">
              <i class="bi bi-building cyber-section-icon"></i>
              Account Summary
            </h3>
          </div>
          <span [class]="'badge ' + getTierBadgeClass() + ' fs-6'">
            <i class="bi" [class]="getTierIcon()"></i>
            {{ accountType }}
          </span>
        </div>
        <div class="welcome-message mb-4">
          <p class="lead">{{ welcomeMessage }}</p>
          @if (isTrialActive()) {
            <div class="trial-info">
              <div class="cyber-alert cyber-alert-info">
                <div class="d-flex">
                  <i class="bi bi-clock-fill flex-shrink-0 me-2"></i>
                  <div>
                    <strong>Trial Period:</strong>
                    {{ getTrialDaysRemaining() }} days remaining
                    <div class="small text-muted mt-1">
                      Your trial includes full access to all premium features.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
        <!-- Account Details -->
        <div class="row">
          <div class="col-md-6">
            <h5 class="mb-3">Account Information</h5>
            <div class="account-details">
              <div class="detail-item">
                <strong>Company:</strong> {{ company?.name || "N/A" }}
              </div>
              @if (user?.email) {
                <div class="detail-item">
                  <strong>Primary Contact:</strong> {{ user?.email }}
                </div>
              }
              @if (user?.firstName && user?.lastName) {
                <div class="detail-item">
                  <strong>Account Owner:</strong> {{ user?.firstName }}
                  {{ user?.lastName }}
                </div>
              }
              @if (company?.industry) {
                <div class="detail-item">
                  <strong>Industry:</strong> {{ company?.industry }}
                </div>
              }
            </div>
          </div>
          <div class="col-md-6">
            <h5 class="mb-3">Account Features</h5>
            <ul class="feature-list">
              @for (feature of accountFeatures; track feature) {
                <li>
                  <i class="bi bi-check-circle-fill text-success me-2"></i>
                  {{ feature }}
                </li>
              }
            </ul>
          </div>
        </div>
      </div>
      <!-- Next Steps -->
      <div class="next-steps-section mb-5">
        <h3 class="cyber-section-title">
          <i class="bi bi-list-check cyber-section-icon"></i>
          Next Steps
        </h3>
        <div class="row">
          <div class="col-12">
            <ol class="next-steps-list">
              @for (step of nextSteps; track step; let i = $index) {
                <li class="step-item">
                  <span class="step-number">{{ i + 1 }}</span>
                  <span class="step-text">{{ step }}</span>
                </li>
              }
            </ol>
          </div>
        </div>
      </div>
      <!-- Support Resources -->
      <div class="support-section mb-5">
        <h3 class="cyber-section-title">
          <i class="bi bi-headset cyber-section-icon"></i>
          Support & Resources
        </h3>
        <div class="row">
          <div class="col-md-4 mb-3">
            <div class="support-card">
              <div class="support-icon">
                <i class="bi bi-envelope-fill"></i>
              </div>
              <h6>Email Support</h6>
              <p class="text-muted small">Get help from our support team</p>
              <button
                class="btn btn-outline-primary btn-sm"
                (click)="contactSupport()"
                >
                Contact Support
              </button>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="support-card">
              <div class="support-icon">
                <i class="bi bi-book-fill"></i>
              </div>
              <h6>Documentation</h6>
              <p class="text-muted small">Learn how to use Scan Ninja</p>
              <button
                class="btn btn-outline-primary btn-sm"
                (click)="openDocumentation()"
                >
                View Docs
              </button>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="support-card">
              <div class="support-icon">
                <i class="bi bi-people-fill"></i>
              </div>
              <h6>Community</h6>
              <p class="text-muted small">Connect with other users</p>
              <button
                class="btn btn-outline-primary btn-sm"
                (click)="openCommunity()"
                >
                Join Community
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Call to Action -->
      <div class="cta-section">
        <div class="text-center">
          <h4 class="mb-3">Ready to Get Started?</h4>
          <p class="text-muted mb-4">
            {{ needsPlan
            ? 'Choose a plan and complete checkout to start scanning.'
            : 'Your dashboard is ready and waiting. Start exploring your security posture today!' }}
          </p>
          <div class="d-flex justify-content-center align-items-center gap-3">
            <button
              class="btn btn-cyber-primary btn-lg"
              (click)="proceedToDashboard()"
              >
              <i class="bi me-2" [class.bi-layers]="needsPlan" [class.bi-speedometer2]="!needsPlan"></i>
              {{ needsPlan ? 'Choose Your Plan' : 'Go to Dashboard' }}
            </button>
            <div class="auto-redirect-info">
              <small class="text-muted">
                Auto-redirecting in {{ redirectTimer }} seconds
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
}
